import * as React from "react";
import { createPortal } from "react-dom";
import { WifiOff } from "lucide-react";
import { useWebSocket } from "../../lib/useWebSocket";
import { useI18n } from "../../lib/i18n";
import * as s from "./RealtimeConnectionIndicator.style.css";

/** Delay before showing the offline chip so brief connect attempts don't flash. */
const SHOW_AFTER_MS = 2500;

/**
 * Floating viewport chip when the pro-api WebSocket is unavailable.
 * Saves still work; live multi-tab updates will not until reconnect.
 */
export function RealtimeConnectionIndicator() {
  const { status, isConnected } = useWebSocket();
  const { t } = useI18n();
  const [visible, setVisible] = React.useState(false);
  const [tooltipOpen, setTooltipOpen] = React.useState(false);

  React.useEffect(() => {
    if (isConnected || status === "idle") {
      setVisible(false);
      return;
    }
    const timer = window.setTimeout(() => setVisible(true), SHOW_AFTER_MS);
    return () => window.clearTimeout(timer);
  }, [isConnected, status]);

  if (!visible || typeof document === "undefined") return null;

  const label = t("pro.realtime.connection.unavailable");
  const hint = t("pro.realtime.connection.unavailableHint");
  const reconnecting = t("pro.realtime.connection.reconnecting");

  return createPortal(
    <div className={s.root}>
      <button
        type="button"
        className={s.chip}
        aria-label={`${label}. ${reconnecting}`}
        title={label}
        onMouseEnter={() => setTooltipOpen(true)}
        onMouseLeave={() => setTooltipOpen(false)}
        onFocus={() => setTooltipOpen(true)}
        onBlur={() => setTooltipOpen(false)}
      >
        <WifiOff size={16} strokeWidth={2.2} aria-hidden />
        <span className={s.chipLabel}>{label}</span>
      </button>
      {tooltipOpen ? (
        <div className={s.tooltip} role="tooltip">
          <div className={s.tooltipTitle}>{label}</div>
          <div className={s.tooltipBody}>{hint}</div>
          <div className={s.tooltipMeta}>{reconnecting}</div>
        </div>
      ) : null}
    </div>,
    document.body,
  );
}
